
import type { Tool } from './Tool';
import { Mixer } from './Mixer';
import { ParticleBrush } from './ParticleBrush';
import { DragTool } from './DragTool';
import { Sand } from '../particles/Sand';
import { Water } from '../particles/Water';
import { Stone } from '../particles/Stone';
import { Empty } from '../particles/Empty';
import { Fire } from '../particles/Fire';
import { Smoke } from '../particles/Smoke';
import { Wood } from '../particles/Wood';
import { Charcoal } from '../particles/Charcoal';
import { Lava } from '../particles/Lava';
import { Heater } from './Heater';
import { Cooler } from './Cooler';
import { Crusher } from './Crusher';
import { GlueTool } from './GlueTool';
import { Gravel } from '../particles/Gravel';
import { Ice } from '../particles/Ice';
import { Steam } from '../particles/Steam';
import { Oil } from '../particles/Oil';
import { Filter } from '../particles/Filter';
import { Saltpeter } from '../particles/Saltpeter';
import { Sulfur } from '../particles/Sulfur';
import { Gunpowder } from '../particles/Gunpowder';
import { Bedrock } from '../particles/Bedrock';

export class ToolRegistry {
    private tools: Map<string, Tool> = new Map();
    private static instance: ToolRegistry;

    private constructor() {
        this.registerTools();
    }

    public static getInstance(): ToolRegistry {
        if (!ToolRegistry.instance) {
            ToolRegistry.instance = new ToolRegistry();
        }
        return ToolRegistry.instance;
    }

    private registerTools() {
        // Drag
        this.tools.set('drag', new DragTool());

        // Glue
        this.tools.set('glue', new GlueTool());

        // Mixer
        this.tools.set('mixer', new Mixer());

        // Eraser
        this.tools.set('eraser', new ParticleBrush('eraser', () => new Empty(), {
            probability: 1.0,
            overwriteRule: () => true // Always overwrite
        }));

        // Sand
        this.tools.set('sand', new ParticleBrush('sand', () => new Sand()));

        // Water
        this.tools.set('water', new ParticleBrush('water', () => new Water()));

        // Stone
        this.tools.set('stone', new ParticleBrush('stone', () => new Stone(), {
            probability: 1.0 // Solid material needs to be dense
        }));

        // Gravel
        this.tools.set('gravel', new ParticleBrush('gravel', () => new Gravel(), {
            probability: 0.8 // Loose material
        }));

        // Wood
        this.tools.set('wood', new ParticleBrush('wood', () => new Wood(), {
            probability: 1 // Solid material needs to be dense
        }));

        // Smoke
        this.tools.set('smoke', new ParticleBrush('smoke', () => new Smoke()));

        // Charcoal
        this.tools.set('charcoal', new ParticleBrush('charcoal', () => new Charcoal()));
        
        // Lava
        this.tools.set('lava', new ParticleBrush('lava', () => new Lava()));

        // Fire (Special rule)
        this.tools.set('fire', new ParticleBrush('fire', () => new Fire(), {
            probability: 1.0, // Fire is aggressive
            overwriteRule: (target) => {
                if (!target || target.empty) return true;
                return target.props.isFlammable;
            }
        }));

        // Heater
        this.tools.set('heater', new Heater());

        // Cooler
        this.tools.set('cooler', new Cooler());

        // Crusher
        this.tools.set('crusher', new Crusher());
        
        // Ice
        this.tools.set('ice', new ParticleBrush('ice', () => new Ice(), {
            probability: 1.0
        }));

        // Steam
        this.tools.set('steam', new ParticleBrush('steam', () => new Steam(), {
            probability: 0.1 // Steam is gas, so it can be sparse
        }));

        // Oil
        this.tools.set('oil', new ParticleBrush('oil', () => new Oil()));

        // Filter
        // this.tools.set('filter', new ParticleBrush('filter', () => new Filter(), {
        //     probability: 1,
        // }));

        // Saltpeter
        this.tools.set('saltpeter', new ParticleBrush('saltpeter', () => new Saltpeter()));

        // Sulfur
        this.tools.set('sulfur', new ParticleBrush('sulfur', () => new Sulfur()));

        // Gunpowder
        this.tools.set('gunpowder', new ParticleBrush('gunpowder', () => new Gunpowder()));

        // Bedrock
        this.tools.set('bedrock', new ParticleBrush('bedrock', () => new Bedrock(), {
            probability: 1.0
        }));
    }

    getTool(name: string): Tool | undefined {
        return this.tools.get(name);
    }
}
